import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, IconButton, CircularProgress, Alert, Dialog,
  DialogTitle, DialogContent, DialogActions, Button, TextField, Avatar,
  Tooltip, Stack, Checkbox, FormControlLabel, Divider, FormGroup,
} from '@mui/material';
import {
  Add, Delete, Edit, Refresh, LockReset, CheckCircle, Cancel,
} from '@mui/icons-material';
import { generateClient } from 'aws-amplify/api';

const client = generateClient();

// ── GraphQL ───────────────────────────────────────────────────────────────────

const LIST_PORTAL_USERS = `
  query ListPortalUsers($limit: Int, $nextToken: String) {
    listPortalUsers(limit: $limit, nextToken: $nextToken) {
      items { username email name status enabled groups createdAt }
      nextToken
    }
  }
`;

const CREATE_PORTAL_USER = `
  mutation CreatePortalUser($input: CreatePortalUserInput!) {
    createPortalUser(input: $input) {
      username email name status enabled groups createdAt
    }
  }
`;

const UPDATE_PORTAL_USER_GROUPS = `
  mutation UpdatePortalUserGroups($input: UpdatePortalUserGroupsInput!) {
    updatePortalUserGroups(input: $input) {
      username email name status enabled groups createdAt
    }
  }
`;

const DELETE_PORTAL_USER = `
  mutation DeletePortalUser($username: String!) {
    deletePortalUser(username: $username) { success message }
  }
`;

const RESET_PORTAL_USER_PASSWORD = `
  mutation ResetPortalUserPassword($username: String!) {
    resetPortalUserPassword(username: $username) { success message }
  }
`;

// ── Role definitions — single source of truth for labels, colors, and access ──

export const ROLES: {
  id: string;
  label: string;
  color: 'error' | 'warning' | 'info' | 'success' | 'secondary';
  description: string;
  access: string[];
}[] = [
  {
    id: 'SuperAdmin',
    label: 'Super Admin',
    color: 'error',
    description: 'Full unrestricted access to all portal features.',
    access: [
      'Dashboard & Analytics',
      'Driver & Vehicle Approvals',
      'Driver Map & Live Positions',
      'Trip Management',
      'User Management (suspend/reactivate)',
      'Cashout Approvals & Rejections',
      'Pricing Configuration (edit)',
      'Service Areas (edit)',
      'SOS Alerts',
      'Error Logs & System Status',
      'Admin Portal User Management (this page)',
    ],
  },
  {
    id: 'Operations',
    label: 'Operations',
    color: 'warning',
    description: 'Day-to-day platform operations.',
    access: [
      'Dashboard & Analytics',
      'Driver & Vehicle Approvals',
      'Driver Map & Live Positions',
      'Trip Management',
      'Service Areas (edit)',
      'SOS Alerts',
      'Send Driver Notifications',
    ],
  },
  {
    id: 'CustomerService',
    label: 'Customer Service',
    color: 'info',
    description: 'Handles rider and driver support requests.',
    access: [
      'Dashboard',
      'Driver List (read-only)',
      'Trip Management (read)',
      'User Management (suspend/reactivate riders)',
      'SOS Alerts',
    ],
  },
  {
    id: 'Finance',
    label: 'Finance',
    color: 'success',
    description: 'Manages payouts and financial reporting.',
    access: [
      'Dashboard & Analytics',
      'Cashout Approvals & Rejections',
      'Trip Management (read)',
      'Pricing Configuration (read-only)',
      'Service Areas (read-only)',
    ],
  },
  {
    id: 'TechSupport',
    label: 'Tech Support',
    color: 'secondary',
    description: 'Monitors system health and investigates errors.',
    access: [
      'Dashboard',
      'Driver List (read-only)',
      'Error Logs',
      'System Status',
    ],
  },
];

// ── Types ─────────────────────────────────────────────────────────────────────

interface PortalUser {
  username: string;
  email: string;
  name?: string;
  status: string;
  enabled: boolean;
  groups: string[];
  createdAt?: string;
}

// ── Role chip ─────────────────────────────────────────────────────────────────

function RoleChip({ roleId }: { roleId: string }) {
  const role = ROLES.find(r => r.id === roleId);
  if (!role) return <Chip label={roleId} size="small" />;
  return <Chip label={role.label} size="small" color={role.color} />;
}

// ── Role selector with access descriptions ────────────────────────────────────

function RoleSelector({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (groups: string[]) => void;
}) {
  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter(g => g !== id) : [...selected, id]);
  };

  return (
    <FormGroup>
      {ROLES.map(role => (
        <Box key={role.id} sx={{ mb: 1.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selected.includes(role.id)}
                onChange={() => toggle(role.id)}
                size="small"
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <Chip label={role.label} size="small" color={role.color} />
                <Typography variant="body2" color="text.secondary">{role.description}</Typography>
              </Box>
            }
          />
          {selected.includes(role.id) && (
            <Box sx={{ ml: 4, mt: 0.5, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
              {role.access.map(a => (
                <Typography key={a} variant="caption" display="block" color="text.secondary">
                  • {a}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      ))}
    </FormGroup>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminPortalUsers() {
  const [users, setUsers] = useState<PortalUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newGroups, setNewGroups] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);

  // Edit groups dialog
  const [editUser, setEditUser] = useState<PortalUser | null>(null);
  const [editGroups, setEditGroups] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [deleteUser, setDeleteUser] = useState<PortalUser | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res: any = await client.graphql({ query: LIST_PORTAL_USERS, variables: { limit: 10 } });
      const conn = res.data?.listPortalUsers;
      setUsers(conn?.items ?? []);
      setNextToken(conn?.nextToken ?? null);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = async () => {
    if (!nextToken || loadingMore) return;
    setLoadingMore(true);
    try {
      const res: any = await client.graphql({ query: LIST_PORTAL_USERS, variables: { limit: 10, nextToken } });
      const conn = res.data?.listPortalUsers;
      setUsers(u => [...u, ...(conn?.items ?? [])]);
      setNextToken(conn?.nextToken ?? null);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load more users');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newEmail.trim() || newGroups.length === 0) return;
    setCreating(true);
    try {
      const res: any = await client.graphql({
        query: CREATE_PORTAL_USER,
        variables: { input: { email: newEmail.trim(), name: newName.trim() || undefined, groups: newGroups } },
      });
      setUsers(u => [...u, res.data.createPortalUser]);
      setSuccess(`User ${newEmail} created. A temporary password has been sent to their email.`);
      setCreateOpen(false);
      setNewEmail(''); setNewName(''); setNewGroups([]);
    } catch (e: any) {
      setError(e.message ?? 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleSaveGroups = async () => {
    if (!editUser || editGroups.length === 0) return;
    setSaving(true);
    try {
      const res: any = await client.graphql({
        query: UPDATE_PORTAL_USER_GROUPS,
        variables: { input: { username: editUser.username, groups: editGroups } },
      });
      setUsers(u => u.map(x => x.username === editUser.username ? res.data.updatePortalUserGroups : x));
      setSuccess(`Roles updated for ${editUser.email}`);
      setEditUser(null);
    } catch (e: any) {
      setError(e.message ?? 'Failed to update roles');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    setActionLoading(deleteUser.username);
    setDeleteUser(null);
    try {
      await client.graphql({ query: DELETE_PORTAL_USER, variables: { username: deleteUser.username } });
      setUsers(u => u.filter(x => x.username !== deleteUser.username));
      setSuccess(`User ${deleteUser.email} deleted`);
    } catch (e: any) {
      setError(e.message ?? 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async (user: PortalUser) => {
    setActionLoading(user.username);
    try {
      await client.graphql({ query: RESET_PORTAL_USER_PASSWORD, variables: { username: user.username } });
      setSuccess(`Password reset email sent to ${user.email}`);
    } catch (e: any) {
      setError(e.message ?? 'Failed to reset password');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h4" gutterBottom>Admin Portal Users</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage who can access this portal and what they can do. Only Super Admins can access this page.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh"><IconButton onClick={load} size="small"><Refresh /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)} size="small">
            Add User
          </Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Role legend */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>Role permissions</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {ROLES.map(role => (
            <Box key={role.id} sx={{ minWidth: 200, flex: '1 1 200px' }}>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <Chip label={role.label} size="small" color={role.color} />
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                {role.description}
              </Typography>
              {role.access.map(a => (
                <Typography key={a} variant="caption" display="block" color="text.disabled" sx={{ lineHeight: 1.6 }}>
                  • {a}
                </Typography>
              ))}
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Users table */}
      <Paper variant="outlined">
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>User</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No portal users found
                    </TableCell>
                  </TableRow>
                )}
                {users.map(u => (
                  <TableRow key={u.username} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'primary.main' }}>
                          {(u.name || u.email)[0].toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>{u.name || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {u.groups.length === 0
                          ? <Chip label="No roles" size="small" color="default" />
                          : u.groups.map(g => <RoleChip key={g} roleId={g} />)
                        }
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {u.enabled
                        ? <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label={u.status} size="small" color="success" variant="outlined" />
                        : <Chip icon={<Cancel sx={{ fontSize: 14 }} />} label="Disabled" size="small" color="error" variant="outlined" />
                      }
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {actionLoading === u.username ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Edit roles">
                            <IconButton size="small" onClick={() => { setEditUser(u); setEditGroups(u.groups); }}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset password">
                            <IconButton size="small" color="warning" onClick={() => handleResetPassword(u)}>
                              <LockReset fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete user">
                            <IconButton size="small" color="error" onClick={() => setDeleteUser(u)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Load more */}
      {nextToken && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Button variant="outlined" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Load more
          </Button>
        </Box>
      )}

      {/* ── Create user dialog ── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Portal User</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email address" fullWidth size="small" type="email"
              value={newEmail} onChange={e => setNewEmail(e.target.value)}
              helperText="A temporary password will be sent to this address"
            />
            <TextField
              label="Full name (optional)" fullWidth size="small"
              value={newName} onChange={e => setNewName(e.target.value)}
            />
            <Divider />
            <Typography variant="subtitle2">Assign roles</Typography>
            <Typography variant="caption" color="text.secondary">
              Select at least one role. Each role grants access to specific pages and actions.
            </Typography>
            <RoleSelector selected={newGroups} onChange={setNewGroups} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !newEmail.trim() || newGroups.length === 0}
          >
            {creating ? <CircularProgress size={18} /> : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit roles dialog ── */}
      <Dialog open={!!editUser} onClose={() => setEditUser(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Roles — {editUser?.email}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Changing roles takes effect immediately on the user's next API call.
            </Typography>
            <RoleSelector selected={editGroups} onChange={setEditGroups} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUser(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveGroups}
            disabled={saving || editGroups.length === 0}
          >
            {saving ? <CircularProgress size={18} /> : 'Save Roles'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete confirm dialog ── */}
      <Dialog open={!!deleteUser} onClose={() => setDeleteUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete <strong>{deleteUser?.email}</strong>? This cannot be undone.
            Their active session will be revoked immediately.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUser(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
